import { ConfidenceMeter } from "@/components/ConfidenceMeter";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, GitBranch, Route, Scale, Sparkles, Target } from "lucide-react";

const DISCIPLINE_COLORS = {
  Reasoning: "text-monk-blue", Textual: "text-emerald-400", Behavioral: "text-purple-300",
  Geographic: "text-amber-300", Biological: "text-rose-300", Digital: "text-cyan-300",
  Legal: "text-orange-300", Visual: "text-pink-300", Active: "text-red-300",
};

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="bg-monk-panel border border-white/10 rounded-md">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Icon className="w-4 h-4 text-neutral-400" />
        <span className="overline">{title}</span>
        {count != null && <span className="ml-auto font-mono text-[11px] text-neutral-500">{count}</span>}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

export function BrainPanel({ analysis }) {
  if (!analysis) return null;
  const leads = analysis.top_leads || [];
  const hunches = analysis.hunches || [];
  const routing = analysis.routing || [];
  const dissent = analysis.dissent || [];
  const bias = analysis.bias_flags || [];
  const crossLinks = analysis.cross_case_links || [];

  return (
    <div className="space-y-4" data-testid="brain-panel">
      {/* Top Leads — Hard Data */}
      <Section icon={Target} title="Top Leads · Hard Data" count={leads.length}>
        {leads.length === 0 && <p className="text-neutral-500 text-sm">No corroborated leads yet.</p>}
        {leads.map((l) => (
          <div key={l.id} className="border border-white/10 rounded-sm p-3 hover:border-white/20 transition-colors" data-testid="lead-item">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="hard-data font-medium text-sm leading-snug">{l.title}</div>
              {l.validated ? (
                <Badge className="bg-monk-blue/15 text-monk-blue border-monk-blue/40 rounded-sm text-[10px] shrink-0">VALIDATED · 3+ DISCIPLINES</Badge>
              ) : (
                <Badge className="bg-white/5 text-neutral-400 border-white/10 rounded-sm text-[10px] shrink-0">UNCORROBORATED</Badge>
              )}
            </div>
            <p className="hard-data text-[13px] text-neutral-300 mb-3 leading-relaxed">{l.detail}</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(l.disciplines || []).map((d, i) => (
                <span key={i} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 ${DISCIPLINE_COLORS[d] || "text-neutral-300"}`}>{d}</span>
              ))}
            </div>
            <ConfidenceMeter value={l.confidence} />
          </div>
        ))}
      </Section>

      {/* Hunches — Tactical Grey Italics (Section 24) */}
      <Section icon={Sparkles} title="Intuition Layer · Hunches" count={hunches.length}>
        {hunches.length === 0 && <p className="text-neutral-500 text-sm">Monk has no active hunches.</p>}
        {hunches.map((h) => (
          <div key={h.id} className="border border-dashed border-white/10 rounded-sm p-3 bg-white/[0.02]" data-testid="hunch-item">
            <p className="hunch-data text-[13px] leading-relaxed mb-2">“{h.text}”</p>
            <ConfidenceMeter value={h.confidence} size="sm" />
          </div>
        ))}
      </Section>

      {/* Evidence Routing (Section 1) */}
      <Section icon={Route} title="Evidence Routing" count={routing.length}>
        {routing.map((r, i) => (
          <div key={i} className="text-[13px]" data-testid="routing-item">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-[11px] text-monk-blue">{r.evidence_ref}</span>
              <span className="text-neutral-500 text-xs">→</span>
              <span className="text-neutral-300 text-xs">{r.note}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(r.engines || []).map((e, j) => (
                <span key={j} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-neutral-300">{e}</span>
              ))}
            </div>
          </div>
        ))}
      </Section>

      {/* Dissent + Bias (Section 3 & 18) */}
      {(dissent.length > 0 || bias.length > 0) && (
        <Section icon={Scale} title="Dissent & Bias Check">
          {dissent.map((d, i) => (
            <div key={i} className="text-[13px] border-l-2 border-monk-warn/50 pl-3" data-testid="dissent-item">
              <p className="hard-data text-neutral-200">{d.point}</p>
              <p className="hunch-data text-xs mt-0.5">{d.why_label || "Outweighed"}: {d.why_outweighed}</p>
            </div>
          ))}
          {bias.map((b, i) => (
            <div key={i} className="flex items-start gap-2 text-[13px] text-monk-warn" data-testid="bias-item">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{b}</span>
            </div>
          ))}
        </Section>
      )}

      {/* Cross-Case Links (CCI Grid / Case Linking) */}
      {crossLinks.length > 0 && (
        <Section icon={GitBranch} title="Cross-Case Links" count={crossLinks.length}>
          {crossLinks.map((c, i) => (
            <div key={i} className="text-[13px] border border-monk-blue/20 bg-monk-blue/[0.03] rounded-sm p-3" data-testid="cross-case-link">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="hard-data font-medium text-monk-blue leading-snug">{c.case}</span>
                {typeof c.confidence === "number" && <span className="font-mono text-[10px] text-neutral-500 shrink-0">{c.confidence}%</span>}
              </div>
              <p className="text-neutral-300 leading-relaxed">{c.connection}</p>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}
